import React, { useState, useEffect, useCallback  } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import moment from 'moment';
import './styles/StudentProfilePage.css';
import { useNavigate } from 'react-router-dom';

const StudentProfilePage = ({isSidebarCollapsed}) => {
    const { studentId } = useParams(); // Get the student ID from URL parameters
    const [student, setStudent] = useState(null);
    const [studentSaving, setStudentSaving] = useState(null);
    const [studentPaymentHistory, setStudentPaymentHistory] = useState(null);
    const [levelHistories, setLevelHistories] = useState([]);
    const [currentGroups, setCurrentGroups] = useState([]);
    const [currentSessions, setCurrentSessions] = useState([]);
    const [groupedSessions, setGroupedSessions] = useState([]);
    const [studentPoint, setStudentPoint] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingGroupType, setPendingGroupType] = useState('');
    const [removalReason, setRemovalReason] = useState('');
    const [isGeneratingQR, setIsGeneratingQR] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStudentDetails = async () => {
            try {
                const response = await api.get(`/student/profile/${studentId}`);
                setStudent(response.data.data.student || null);
                setLevelHistories(response.data.data.studentLevelHistoryList || null);
            } catch (error) {
                console.error("Failed to fetch student details", error);
            }
        };
        fetchStudentDetails();

        const fetchStudentSavingsDetails = async () => {
            try {
                const response = await api.get(`/saving/detail/${studentId}`);
                setStudentSaving(response.data.message ? null : response.data.data.studentSaving);
            } catch (error) {
                console.error("Failed to fetch student details", error);
            }
        };
        fetchStudentSavingsDetails();

        const fetchStudentPaymentHistories = async () => {
            try {
                const response = await api.get(`/payment/sppList/${studentId}`);
                setStudentPaymentHistory(Array.isArray(response.data.data?.[0]) ? response.data.data[0] : response.data.data || []);
            } catch (error) {
                console.error("Failed to fetch student payment history details", error);
            }
        };
        fetchStudentPaymentHistories();
        
        const fetchStudentActiveSessions = async () => {
            try {
                const response = await api.get(`/schedule/check-active/${studentId}`);
                const groups = response.data.data || [];
                setCurrentGroups(groups);
                
                if (groups.length > 0) {
                    const scheduleRes = await api.get(`/schedule/student/${studentId}`);
                    setCurrentSessions(scheduleRes.data.data || []);
                    
                } else {
                    setCurrentSessions([]);
                }
                
            } catch (error) {
                console.error("Failed to fetch student details", error);
            }
        };
        fetchStudentActiveSessions();

        const fetchStudentPoint = async () => {
            try {
                const response = await api.post('student/point/getPoint', {
                    data: { student_id: Number(studentId) }
                });
                setStudentPoint(response.data.data.studentPoint || null);
            } catch (error) {
                console.error("Failed to fetch student points", error);
            }
        };
        fetchStudentPoint();
    }, [studentId]);

    useEffect(() => {
        if (currentGroups.length === 0 || currentSessions.length === 0) return;
    
        const grouped = currentGroups.map(group => {
            const sessionsForGroup = currentSessions.filter(
                session => session.group === group.group_id
            );
    
            return {
                group_id: group.group_id,
                group_type: group.group_type,
                sessions: sessionsForGroup
            };
        });
    
        setGroupedSessions(grouped);
    }, [currentGroups, currentSessions]);

    const refreshStudentPoint = useCallback(async () => {
        try {
            const res = await api.post('student/point/getPoint', {
            data: { student_id: Number(studentId) }
            });
            setStudentPoint(res?.data?.data?.studentPoint ?? null);
        } catch {
            setStudentPoint(null);
        }
    }, [studentId]);

    const handleGenerateStudentQR = async () => {
        if (isGeneratingQR) return;
        setIsGeneratingQR(true);
        try {
            // 1) Create student point (aktifkan)
            const res = await api.post('student/point/create', {
            data: { student_id: Number(studentId) }
            });

            const { student_id, student_name } = res?.data?.data || {};
            if (!student_id || !student_name) {
            throw new Error('Invalid response');
            }

            // 2) Buat payload QR yang stabil
            const payload = `sid=${student_id}|name=${String(student_name).trim()}`;
            
            // 3) Generate PNG & auto-download (pakai lib 'qrcode')
            const QRCode = (await import('qrcode')).default;        // dynamic import OK
            const dataUrl = await QRCode.toDataURL(payload, {
            errorCorrectionLevel: 'M',
            margin: 1,
            scale: 8, // resolusi lebih tajam
            });

            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `student-qr-${student_name}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            // 4) Refresh section Student Point supaya berubah dari “not enabled” ke data poin
            await refreshStudentPoint();
        } catch (e) {
            console.error(e);
            alert('Failed to create student point or generate QR.');
        } finally {
            setIsGeneratingQR(false);
        }
    };


    if (!student) return <p>Loading student details...</p>;

    const showPaymentButton = () => {
        const status = student.StudentStatuses[0].Status.status_name;
        if (status === 'NONACTIVE') return false; 
        else return true;
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount).toLocaleString('id-ID', { minimumFractionDigits: 0 });
    };

    const simulateProgress = (onComplete) => {
        setProgress(0);
        let percent = 0;
        const interval = setInterval(() => {
            percent += Math.random() * 10;
            if (percent >= 100) {
                clearInterval(interval);
                setProgress(100);
                if (typeof onComplete === 'function') {
                    setTimeout(() => {
                        onComplete(); // panggil setelah 100ms supaya UI sempat render 100%
                    }, 100);
                }
            } else {
                setProgress(Math.floor(percent));
            }
        }, 100);
    };
    
      

    const handleSubmit = async (groupType, reason) => {
        setShowReasonModal(false);
        setIsLoading(true);
        
    
        const body = {
            student_id: student.student_id,
            group_type: groupType,
            reason: reason,
        };
    
    
        try {
            await api.post('/schedule/takeout', body);
            const successMessage = 'Takeout Student Successfully!';
            simulateProgress(() => {
                setIsLoading(false);
                alert(successMessage);
                navigate(0);
            });
        } catch (err) {
            console.error(err);
            navigate('/error', { state: { message: 'Please ask to administrator!' } });
        }
    };
    

    return (
        <div className={`page-content ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="page-title">Student profile</div>
            <div className="form-container">
                <div className="section">
                    <h3 className="section-header">Student Data</h3>
                    <table>
                        <tbody>
                            <tr>
                                <td>Name</td>
                                <td>:</td>
                                <td>{student.student_name}</td>
                            </tr>
                            <tr>
                                <td>NIK</td>
                                <td>:</td>
                                <td>{student.student_code}</td>
                            </tr>
                            <tr>
                                <td>Birth Date</td>
                                <td>:</td>
                                <td>{moment(student.birth_date).format('DD MMMM YYYY')}</td>
                            </tr>
                            <tr>
                                <td>Branch</td>
                                <td>:</td>
                                <td>{student.Branch.branch_name}</td>
                            </tr>
                            <tr>
                                <td>Address</td>
                                <td>:</td>
                                <td>{student.address}</td>
                            </tr>
                            <tr>
                                <td>Join Date</td>
                                <td>:</td>
                                <td>{moment(student.join_date).format('DD MMMM YYYY')}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="section">
                    <h3 className="section-header">Contact Person Data</h3>
                    <div className="create-student-table">
                        <table>
                            <thead>
                            <tr>
                                <th className="no-column">No</th>
                                <th className='table-center-align'>Contact Person Name</th>
                                <th className='table-center-align'>Relationship</th>
                                <th className='table-center-align'>Phone Number	</th>
                            </tr>
                            </thead>
                            <tbody>
                            {/* Tampilkan data dari costs */}
                            {student.StudentContactPeople.map((cp, index) => (
                                <tr key={cp.student_contact_person_id}>
                                    <td>{index + 1}</td>
                                    <td>{cp.contact_person_name}</td>
                                    <td>{cp.relationship}</td>
                                    <td>{cp.phone_number}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="section">
                    <h3 className="section-header">Program Data</h3>
                    <table>
                        <tbody>
                            <tr>
                                <td>Program</td>
                                <td>:</td>
                                <td>{student.StudentLevels[0].mainProgram.program_name}</td>
                            </tr>
                            <tr>
                                <td>Level</td>
                                <td>:</td>
                                <td>{student.StudentLevels[0].Level.level_name}</td>
                            </tr>
                            <tr>
                                <td>Add On Program</td>
                                <td>:</td>
                                <td>{student.StudentLevels[0].addonProgram?student.StudentLevels[0].addonProgram.program_name : "-"}</td>
                            </tr>
                            <tr>
                                <td>Status</td>
                                <td>:</td>
                                <td>{student.StudentStatuses.map(status => status.Status.status_name).join(', ')}</td>
                            </tr>
                            <tr>
                                <td>Last Payment Date</td>
                                <td>:</td>
                                <td>{moment(student.Payments.map(payment => payment.payment_date).join(', ')).format('DD MMMM YYYY')}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {studentPaymentHistory && (
                    <div className="section">
                        <h3 className="section-header">Student Payment History</h3>
                        <div className="create-student-table">
                        <table>
                            <thead>
                            <tr>
                                <th className="no-column">No</th>
                                <th className="table-center-align">SPP Month</th>
                                <th className="table-center-align">Payment Date</th>
                                <th className="table-center-align">Submitted By</th>
                                <th className="table-center-align">Submitted On</th>
                            </tr>
                            </thead>
                            <tbody>
                            {studentPaymentHistory
                                .flatMap(payment =>
                                (payment.StudentPaymentHistories || []).map(history => ({
                                    ...history,
                                    payment_date: payment.payment_date,
                                }))
                                )
                                .sort((a, b) => b.student_payment_history_id - a.student_payment_history_id)
                                .map((item, index) => {
                                const formatDate = (dateStr, withTime = false) => {
                                    if (!dateStr) return '-';
                                    const date = new Date(dateStr);
                                    return withTime
                                    ? date.toLocaleString('en-GB', {
                                        day: '2-digit',
                                        month: 'long',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        fractionalSecondDigits: 3,
                                        hour12: false,
                                        })
                                    : date.toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric',
                                        });
                                };

                                return (
                                    <tr key={item.student_payment_history_id}>
                                    <td>{index + 1}</td>
                                    <td>{`${new Date(item.payment_year, item.payment_month - 1).toLocaleString('en-GB', {
                                        month: 'long',
                                        year: 'numeric',
                                    })}`}</td>
                                    <td>{formatDate(item.payment_date)}</td>
                                    <td>{item.created_by}</td>
                                    <td>{formatDate(item.created_on, true)}</td>
                                    </tr>
                                );
                                })}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
                <div className="section">
                    <h3 className="section-header">Level History</h3>
                    <div className="create-student-table">
                        <table>
                            <thead>
                            <tr>
                                <th className="no-column">No</th>
                                <th className='table-center-align'>Program</th>
                                <th className='table-center-align'>Level</th>
                                <th className='table-center-align'>Start Level</th>
                                <th className='table-center-align'>Finish Level</th>
                            </tr>
                            </thead>
                            <tbody>
                            {/* Tampilkan data dari costs */}
                            {levelHistories.map((history, index) => (
                                <tr key={history.student_level_history_id}>
                                <td>{index + 1}</td>
                                <td>{history.Level.Program.program_name}</td>
                                <td>{history.Level.level_name}</td>
                                <td>{moment(history.start_date).format('DD MMMM YYYY')}</td>
                                <td>{history.end_date ? moment(history.end_date).format('DD MMMM YYYY') : 'Present'}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                {groupedSessions.length > 0 && (
                    <div className="section">
                        <h3 className="section-header">Student Schedule</h3>
                        {groupedSessions.map((group, index) => (
                            <div key={index}>
                                <h4>{group.group_type}</h4>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td><h4><button className="profile-button" onClick={() => { setPendingGroupType(group.group_type); setRemovalReason(''); setShowReasonModal(true); }}>Remove from Schedule</button></h4></td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div className="create-student-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Day</th>
                                                <th>Time</th>
                                                <th>Teacher</th>
                                                <th>Session Type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.sessions.map((s, i) => (
                                                <tr key={i}>
                                                    <td>{s.day}</td>
                                                    <td>{s.time}</td>
                                                    <td>{s.teacher?.name || 'Unknown'}</td>
                                                    <td>{s.session_type}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <h4> </h4>
                                </div>
                            </div>
                        ))}
                    </div>
                )}{groupedSessions.length == 0 && (
                    <div className="section">
                        <h3 className="section-header">Student Schedule</h3>
                        <table>
                            <tbody>
                                <tr>
                                    <td><b>No schedule has been assigned to this student yet. Please review or assign a schedule if needed.</b></td>
                                </tr>
                            </tbody>
                    </table>
                    </div>
                )}
                {studentPoint != null && (
                    <div className="section">
                        <h3 className="section-header">Student Point</h3>
                        <table>
                            <tbody>
                                <tr>
                                    <td>Point Balance</td>
                                    <td>:</td>
                                    <td>{studentPoint.point_balance}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="create-student-table">
                            <table>
                                <thead>
                                <tr>
                                    <th className="no-column">No</th>
                                    <th className='table-center-align'>Point Type</th>
                                    <th className='table-center-align'>Amount</th>
                                    <th className='table-center-align'>Point Date</th>
                                    <th className='table-center-align'>Scanned By</th>
                                </tr>
                                </thead>
                                <tbody>
                                {studentPoint.studentPointHistories.map((point, index) => (
                                    <tr key={point.student_point_history_id}>
                                    <td>{index + 1}</td>
                                    <td>{point.point_type}</td>
                                    <td>{point.points_changed}</td>
                                    <td>{moment(point.point_date).format('DD MMMM YYYY')}</td>
                                    <td>{point.created_by}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            <div className="button-group-sub-menu">
                                <button
                                    className="prsub-menu-button btn-qr"
                                    onClick={handleGenerateStudentQR}
                                    disabled={isGeneratingQR}
                                    >
                                    {isGeneratingQR ? 'Generating…' : 'Generate Student QR'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}{studentPoint == null && (
                    <div className="section">
                        <h3 className="section-header">Student Point</h3>
                        <table>
                            <tbody>
                                <tr>
                                    <td><b>Points aren’t enabled yet. Click “Generate QR” to activate this student points.</b></td>
                                </tr>
                            </tbody>
                            
                        </table>
                        <div className="button-group-sub-menu">
                            <button
                                className="prsub-menu-button btn-qr"
                                onClick={handleGenerateStudentQR}
                                disabled={isGeneratingQR}
                                >
                                {isGeneratingQR ? 'Generating…' : 'Generate Student QR'}
                            </button>
                        </div>
                    </div>
                )}
                {studentSaving &&
                <div className="section">
                    <h3 className="section-header">Student Savings History</h3>
                    <table>
                        <tbody>
                            <tr>
                                <td>Total Amount</td>
                                <td>:</td>
                                <td>{formatCurrency(studentSaving.amount)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="create-student-table">
                        <table>
                            <thead>
                            <tr>
                                <th className="no-column">No</th>
                                <th className='table-center-align'>Amount</th>
                                <th className='table-center-align'>Transaction Type</th>
                                <th className='table-center-align'>Transaction Date</th>
                                <th className='table-center-align'>Description</th>
                            </tr>
                            </thead>
                            <tbody>
                            {/* Tampilkan data dari costs */}
                            {studentSaving.StudentSavingsDetails.map((history, index) => (
                                <tr key={history.student_savings_detail_id}>
                                <td>{index + 1}</td>
                                <td>{formatCurrency(history.amount)}</td>
                                <td>{history.transaction_type}</td>
                                <td>{moment(history.transaction_date).format('DD MMMM YYYY')}</td>
                                <td>{history.description}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                }
                
                <div className="button-group-profile">
                    <button className="profile-button" onClick={() => navigate(`/student/edit/${student.student_id}`)}>Edit</button>
                    <button className="profile-button" onClick={() => navigate('/students')}>Back</button>
                    {showPaymentButton() && <button className="profile-button" onClick={() => navigate(`/spp-payment-details/${student.student_id}`)}>Payment</button>}
                    {studentSaving && <button className="profile-button" onClick={() => navigate(`/payment/graduation/${student.student_id}`)}>Payment Graduation</button>}
                </div>
            </div>
            {showReasonModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                    <h3 className="modal-title">🗑 Confirm Removal</h3>
                    <p>
                        You're about to remove this student from <strong>{pendingGroupType}</strong> class.
                        Please provide a reason below:
                    </p>
                    <label className="modal-label">
                        Reason:
                        <textarea
                        value={removalReason}
                        onChange={(e) => setRemovalReason(e.target.value)}
                        rows={4}
                        placeholder="e.g. Class discontinued, duplicate entry, etc."
                        className="modal-textarea"
                        />
                    </label>
                    <div className="modal-actions">
                        <button
                        className="profile-button danger"
                        onClick={() => handleSubmit(pendingGroupType, removalReason)}
                        disabled={!removalReason.trim()}
                        title={!removalReason.trim() ? "Reason is required" : ""}
                        >
                        Confirm Remove
                        </button>
                        <button
                        className="profile-button cancel"
                        onClick={() => setShowReasonModal(false)}
                        >
                        Cancel
                        </button>
                    </div>
                    </div>
                </div>
            )}

            {isLoading && (
            <div className="progress-overlay">
                <div className="progress-container">
                <div className="progress-bar" style={{ width: `${progress}%` }} />
                <div className="progress-text">{progress}%</div>
                </div>
            </div>
            )}
        </div>
    );
};

export default StudentProfilePage;
